import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';

export const RedFlagsTab = ({
  redFlaggedUsers,
  blockedAttempts,
  selectedRedFlagUsers,
  setSelectedRedFlagUsers,
  loadingRedFlags,
  onEnableAlerts,
  onDisableAlerts,
  onBulkClearRedFlags
}) => {
  if (loadingRedFlags) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">Loading red flag data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-3xl text-red-600 mb-4" 
          style={{ 
            fontFamily: 'Bebas Neue, sans-serif',
            letterSpacing: '4px',
            textShadow: 'none',
            fontWeight: '400'
          }}>
        🚩 RED FLAGGED USERS
      </h2>
      
      <p className="text-gray-700 mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
        Users who have triggered 3 or more alerts within a 2-hour period are automatically flagged and disabled from sending alerts.
        You can re-enable their alert privileges below.
      </p>

      {/* Bulk Actions for Red Flags */}
      {selectedRedFlagUsers.length > 0 && (
        <div className="flex gap-3 mb-4">
          <Button
            onClick={onBulkClearRedFlags}
            className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 border-3 border-black"
            style={{
              fontFamily: 'Fredoka, sans-serif',
              letterSpacing: '0.05em',
              textTransform: 'uppercase'
            }}
          >
            ✅ Re-Enable Alerts for Selected ({selectedRedFlagUsers.length})
          </Button>
        </div>
      )}

      {/* Red Flagged Users Table */}
      <div className="bg-white rounded border-2 border-black overflow-x-auto">
        <table className="w-full">
          <thead className="bg-red-100 border-b-2 border-black">
            <tr>
              <th className="p-3 text-left w-12">
                <Checkbox
                  checked={selectedRedFlagUsers.length === redFlaggedUsers.length && redFlaggedUsers.length > 0}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelectedRedFlagUsers(redFlaggedUsers.map(u => u.id));
                    } else {
                      setSelectedRedFlagUsers([]);
                    }
                  }}
                  className="border-red-800"
                />
              </th>
              <th className="p-3 text-left font-bold text-red-800">User</th>
              <th className="p-3 text-left font-bold text-red-800">Email</th>
              <th className="p-3 text-left font-bold text-red-800">Reason</th>
              <th className="p-3 text-left font-bold text-red-800">Flagged Date</th>
              <th className="p-3 text-left font-bold text-red-800">Status</th>
              <th className="p-3 text-left font-bold text-red-800">Actions</th>
            </tr>
          </thead>
          <tbody>
            {redFlaggedUsers.length === 0 ? (
              <tr>
                <td colSpan={7} className="p-8 text-center text-gray-500">
                  No red-flagged users found
                </td>
              </tr>
            ) : (
              redFlaggedUsers.map((u, idx) => (
                <tr key={u.id} className={`border-b border-gray-200 ${idx % 2 === 0 ? 'bg-white' : 'bg-red-50'}`}>
                  <td className="p-3">
                    <Checkbox
                      checked={selectedRedFlagUsers.includes(u.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedRedFlagUsers([...selectedRedFlagUsers, u.id]);
                        } else {
                          setSelectedRedFlagUsers(selectedRedFlagUsers.filter(id => id !== u.id));
                        }
                      }}
                    />
                  </td>
                  <td className="p-3 font-semibold">{u.name}</td>
                  <td className="p-3 text-gray-600">{u.email}</td>
                  <td className="p-3 text-red-600 text-sm">{u.red_flag_reason || 'N/A'}</td>
                  <td className="p-3 text-gray-600 text-sm">
                    {u.red_flag_date ? new Date(u.red_flag_date).toLocaleString() : 'N/A'}
                  </td>
                  <td className="p-3">
                    {u.alert_disabled ? (
                      <span className="px-2 py-1 bg-red-600 text-white text-xs font-bold rounded">DISABLED</span>
                    ) : (
                      <span className="px-2 py-1 bg-yellow-500 text-white text-xs font-bold rounded">FLAGGED</span>
                    )}
                  </td>
                  <td className="p-3">
                    {u.alert_disabled ? (
                      <Button
                        type="button"
                        onClick={() => onEnableAlerts(u.id, u.name)}
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white text-xs"
                      >
                        Re-Enable Alerts
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        onClick={() => onDisableAlerts(u.id, u.name)}
                        size="sm"
                        className="bg-red-600 hover:bg-red-700 text-white text-xs"
                      >
                        Disable Alerts
                      </Button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="text-sm text-gray-600">
        Total red-flagged users: <strong>{redFlaggedUsers.length}</strong>
      </div>

      {/* Blocked Alert Attempts Section */}
      <div className="mt-8">
        <h3 className="text-2xl text-red-600 mb-4" 
            style={{ 
              fontFamily: 'Bebas Neue, sans-serif',
              letterSpacing: '3px'
            }}>
          BLOCKED ALERT ATTEMPTS
        </h3>
        
        <p className="text-gray-700 mb-4 text-sm" style={{ fontFamily: 'Fredoka, sans-serif' }}>
          Log of alert attempts that were blocked due to rate limiting (1 alert per 2 hours per user).
        </p>

        <div className="bg-white rounded border-2 border-black overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full">
            <thead className="bg-orange-100 border-b-2 border-black sticky top-0">
              <tr>
                <th className="p-3 text-left font-bold text-orange-800">User</th>
                <th className="p-3 text-left font-bold text-orange-800">Email</th>
                <th className="p-3 text-left font-bold text-orange-800">Attempt Time</th>
                <th className="p-3 text-left font-bold text-orange-800">Reason</th>
              </tr>
            </thead>
            <tbody>
              {blockedAttempts.length === 0 ? (
                <tr>
                  <td colSpan={4} className="p-8 text-center text-gray-500">
                    No blocked attempts recorded
                  </td>
                </tr>
              ) : (
                blockedAttempts.slice(0, 100).map((attempt, idx) => (
                  <tr key={attempt.id} className={`border-b border-gray-200 ${idx % 2 === 0 ? 'bg-white' : 'bg-orange-50'}`}>
                    <td className="p-3 font-semibold">{attempt.user_name}</td>
                    <td className="p-3 text-gray-600 text-sm">{attempt.user_email}</td>
                    <td className="p-3 text-gray-600 text-sm">
                      {new Date(attempt.attempt_time).toLocaleString()}
                    </td>
                    <td className="p-3 text-orange-600 text-sm">{attempt.reason}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="text-sm text-gray-600 mt-2">
          Total blocked attempts: <strong>{blockedAttempts.length}</strong>
          {blockedAttempts.length > 100 && <span> (showing first 100)</span>}
        </div>
      </div>
    </div>
  );
};
